import React from "react";
import PropTypes from "prop-types";
import Chatkit from "@pusher/chatkit-client";
import { useChatDependencyContext } from "./chat-dependency-context";
import { makeChatKitUserId } from "../utils/utils";

const CHATKIT_INSTANCE_LOCATOR = "v1:us1:b13fe5e9-3e97-46db-9ed9-d6c486536ed1";
const STAGE_CHATKIT_INSTANCE_LOCATOR = "v1:us1:4a5b6caf-c591-4d42-860f-005767e2327f";

const ChatCurrentUserContext = React.createContext();

const ChatCurrentUserProvider = ({ children, ...props }) => {
  const [loading, setLoading] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState({});
  const { appKey, authorization, userId, oid, env } = useChatDependencyContext();

  React.useEffect(() => {
    if (!oid) return;
    setLoading(true);

    const ET_CHATKIT_AUTH = `https://${env === "production" ? "" : "stage-"}api.evertrue.com/chat/v1/token`;
    const instance_locator = env !== "production" ? STAGE_CHATKIT_INSTANCE_LOCATOR : CHATKIT_INSTANCE_LOCATOR;

    const chatkit_token_provider = new Chatkit.TokenProvider({
      url: ET_CHATKIT_AUTH,
      queryParams: {
        oid,
      },
      headers: {
        "Application-Key": appKey,
        Authorization: authorization,
        "Authorization-Provider": "EverTrueAuthToken",
      },
    });

    const chatManager = new Chatkit.ChatManager({
      instanceLocator: instance_locator,
      userId: makeChatKitUserId(oid, userId),
      tokenProvider: chatkit_token_provider,
    });

    chatManager
      .connect()
      .then(user => {
        setCurrentUser(user);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        console.error({ error: e });
      });
  }, [appKey, authorization, env, oid, userId]);

  // currentUser is only fetched once and put in state so referential equality works
  const value = React.useMemo(() => {
    return { currentChatUser: currentUser, loading };
  }, [currentUser, loading]);

  return <ChatCurrentUserContext.Provider value={value}>{children}</ChatCurrentUserContext.Provider>;
};

ChatCurrentUserProvider.propTypes = { children: PropTypes.any };

const useChatCurrentUser = () => React.useContext(ChatCurrentUserContext);

export { ChatCurrentUserContext, useChatCurrentUser, ChatCurrentUserProvider };

export default ChatCurrentUserContext;
